import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Heading, Modal } from '@zillow/constellation-10';

import PropertiesActions from '../../../actions/PropertiesActions';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';

import MultiplexWarningBanner from '../../../../../src/components/MultiplexWarningBanner';
import PaymentDetails from '../../Payment/PaymentDetails';

import type { FreemiumChargeResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { FreemiumDatarouterError } from '../FreemiumErrorModal';

type Props = {
  handleFreemiumChargeSuccess: (response: FreemiumChargeResponseWebDto) => void;
  onFreemiumChargeError: (error: FreemiumDatarouterError) => void;
  listingAlias: string;
  isRenderedInPageView: boolean;
  onClose: () => void;
  onPaymentMethodChange?: () => void;
  isInBuildingGroup?: boolean;
};

/**
 * We want to disable closing the modal when a payment is processing.
 * We add the logic here as we need to prevent outside click, esc, and close btn click actions
 * and the logic needed is within the child component.
 *
 * Once the payment is complete we close the modal automatically from PremiumPaymentGateway
 * via component removal
 *
 */

const PaymentModal = ({
  handleFreemiumChargeSuccess,
  onFreemiumChargeError,
  listingAlias,
  isRenderedInPageView,
  onClose,
  onPaymentMethodChange,
  isInBuildingGroup = false,
}: Props) => {
  const dispatch = useDispatch();

  const [isChargeFetching, setIsChargeFetching] = useState(true);
  const [headingText, setHeadingText] = useState('Credit card information');

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // If user closes modal without upgrading, reset the selected product type to free
      dispatch(
        PropertiesActions.updateCurrentProperty({
          selectedProductType: PRODUCT_TYPE_NAME_TO_ID.FREE,
        }),
      );
      onClose();
    }
  };

  return (
    <Modal
      useDismissProps={{ escapeKey: isChargeFetching === false, outsidePress: isChargeFetching === false }}
      closeButton={isChargeFetching === false ? undefined : <span hidden />}
      header={<Heading level={2}>{headingText}</Heading>}
      body={
        <>
          <MultiplexWarningBanner isInBuildingGroup={isInBuildingGroup} />
          <PaymentDetails
            listingAlias={listingAlias}
            isRenderedInPageView={isRenderedInPageView}
            handleFreemiumChargeSuccess={handleFreemiumChargeSuccess}
            onFreemiumChargeError={onFreemiumChargeError}
            onPaymentMethodChange={onPaymentMethodChange}
            setIsChargeFetching={setIsChargeFetching}
            setHeadingText={setHeadingText}
          />
        </>
      }
      onOpenChange={handleOpenChange}
      open={true}
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default PaymentModal;
